// ── Pager: Gmail-style prev/next arrows + "X–Y of Z" range ──
//
// Pure module (no DOM, no api.js) so node:test can import it without stubs. Owners keep
// their own { offset, limit, total } and re-render this markup after every fetch; clicks
// are handled by the owner's delegated listener on `[data-pager-scope="<scope>"][data-pager]`
// — the scope keeps two pagers on one page (dashboard My Tasks + All Tasks) from
// answering each other's clicks. Used by pages/dashboard.js and pages/workspace-tasks.js.

// Same 24×24 stroke chevrons the sidebar draws (components/sidebar.js keeps its ICONS
// table private, so the two paths are duplicated here rather than widening that surface).
const CHEVRON_LEFT = '<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>';
const CHEVRON_RIGHT = '<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>';

function toInt(n, fallback = 0) {
  const v = Number(n);
  return Number.isFinite(v) ? Math.max(0, Math.floor(v)) : fallback;
}

// Last-page offset when `offset` points past the end (a row left the list, the count
// shrank under a new filter) — 0 when there is nothing to show. Never returns a value
// that is not a multiple of `limit`, so pages stay aligned.
export function clampOffset(offset, limit, total) {
  const lim = Math.max(1, toInt(limit, 1));
  const tot = toInt(total);
  const off = toInt(offset);
  if (tot === 0) return 0;
  if (off < tot) return off - (off % lim);
  return Math.floor((tot - 1) / lim) * lim;
}

// { start, end, hasPrev, hasNext, page, pages } for a 1-based "start–end of total" caption.
// start/end are 0 when total is 0.
export function pageRange({ offset, limit, total }) {
  const lim = Math.max(1, toInt(limit, 1));
  const tot = toInt(total);
  const off = clampOffset(offset, lim, tot);
  const start = tot ? off + 1 : 0;
  const end = Math.min(off + lim, tot);
  const pages = Math.ceil(tot / lim);
  return {
    start,
    end,
    hasPrev: off > 0,
    hasNext: off + lim < tot,
    page: tot ? Math.floor(off / lim) + 1 : 0,
    pages,
  };
}

// Markup for one pager. '' when there is nothing to page (owner hides the slot). `disabled`
// greys both arrows while a fetch is in flight so a double-click cannot skip a page.
export function renderPager({ offset, limit, total, scope, disabled = false }) {
  const r = pageRange({ offset, limit, total });
  if (!r.end) return '';
  const s = String(scope || '').replace(/[^a-z0-9_-]/gi, '');
  const btn = (dir, svg, enabled, label) =>
    `<button type="button" class="pager-btn" data-pager="${dir}" data-pager-scope="${s}" aria-label="${label}"${enabled && !disabled ? '' : ' disabled'}>${svg}</button>`;
  return `<div class="pager" data-pager-scope="${s}">`
    + `<span class="pager-range" aria-live="polite">${r.start}–${r.end} of ${total}</span>`
    + btn('prev', CHEVRON_LEFT, r.hasPrev, 'Previous page')
    + btn('next', CHEVRON_RIGHT, r.hasNext, 'Next page')
    + '</div>';
}
